import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { getDetailPolicyAction, updatePolicyAction } from '../../../redux/actions/policyActions';
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Grid
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DatePickerField from '../../../common/DatePickerDashboard';
import dayjs from 'dayjs';

const policyTypeOptions = [
  { value: 'New Business', label: 'New Business' },
  { value: 'Renewal', label: 'Renewal' },
  { value: 'Rewrite', label: 'Rewrite' }
];

const termOptions = [
  { value: '6', label: '6 Months' },
  { value: '12', label: '12 Months' }
];

const lineOfBusinessOptions = [
  { value: 'Health', label: 'Health' },
  { value: 'Homeowners', label: 'Homeowners' },
  { value: 'Commercial Auto', label: 'Commercial Auto' },
  { value: 'Personal Auto', label: 'Personal Auto' },
  { value: 'General Liability', label: 'General Liability' }
];

const PolicyDetail = ({policyId, data}) => {
  const dispatch = useDispatch();
  const policyInfoDetail = useSelector((state) => state?.policy?.allDetailPolicy || null);
  const fetchedOnce = useRef(false);

  const [openEdit, setOpenEdit] = useState(false);
  const [formData, setFormData] = useState({});

  const calculateExpirationDate = (effectiveDate, term) => {
    if (effectiveDate && term) {
      const termMonths = parseInt(term, 10);
      return dayjs(effectiveDate, 'MM-DD-YYYY').add(termMonths, 'month').format('MM-DD-YYYY');
    }
    return null;
  };

  useEffect(() => {
    if (data) setFormData(data);
  }, [data]);

  useEffect(() => {
    if (data || !policyId || fetchedOnce.current) return;
    fetchedOnce.current = true;
    (async () => {
      // await dispatch(getDetailPolicyAction(policyId));
      dispatch({ type: "RESET_GET_DETAIL_POLICY_SUCCESS" });
    })();
  }, [dispatch, policyId, data]);

  useEffect(() => {
    if (!data && policyInfoDetail) setFormData(policyInfoDetail);
  }, [policyInfoDetail, data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'term') {
      const updatedFormData = {
        ...formData,
        [name]: value
      };
      
      if (updatedFormData.effectiveDate) {
        const expirationDate = calculateExpirationDate(updatedFormData.effectiveDate, value);
        updatedFormData.expirationDate = expirationDate;
      }
      
      setFormData(updatedFormData);
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleDateChange = (field, value) => {
    const updatedFormData = {
      ...formData,
      [field]: value ? dayjs(value).format("MM-DD-YYYY") : null
    };

    if (field === 'effectiveDate' && formData.term) {
      const expirationDate = calculateExpirationDate(value, formData.term);
      updatedFormData.expirationDate = expirationDate;
    }

    setFormData(updatedFormData);
  };

  const handleUpdate = () => {
    let payload = {
      ...policyInfoDetail,
      ...formData
    };

    payload.id = policyInfoDetail.id;
    payload.clientCustomerId = policyInfoDetail.clientCustomerId;

    if (payload.phoneNumber) {
      let digits = payload.phoneNumber.replace(/\D/g, "");
      if (!digits.startsWith("1")) {
        digits = "1" + digits;
      }
      payload.phoneNumber = "+" + digits;
    }

    dispatch(updatePolicyAction(payload))
      .then(() => {
        setOpenEdit(false);
        return dispatch(getDetailPolicyAction(policyInfoDetail.id));
      })
      .catch((err) => console.error("❌ Failed to update policy:", err));
  };

  const RequiredLabel = ({ children, required = false }) => (
    <label className='fnt-we'>
        {children}
        {required && <span style={{ color: '#d32f2f', marginLeft: '4px' }}>*</span>}
    </label>
  );

  return (
    <>
      <Table sx={{ verticalAlign: "Top", padding: "0 10px", boxShadow: "none", backgroundColor: "#ffffff" }}>
        <TableBody>
          <TableRow sx={{ verticalAlign: "top" }}>
            <TableCell
              colSpan={2}
              sx={{
                textAlign: "right",
                verticalAlign: "top",
                paddingTop: "4px", // optional: reduce top padding so it sticks higher
            }}
            >
              <IconButton onClick={() => setOpenEdit(true)}>
                <EditIcon sx={{ color: "black" }}  />
              </IconButton>
            </TableCell>
          </TableRow>


          <TableRow sx={{ verticalAlign: "Top" }}>
            <TableCell>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{fontFamily: `"Poppins", sans-serif`,}}><strong>Policy Type:</strong></TableCell>
                    <TableCell sx={{fontFamily: `"Poppins", sans-serif`,}}>{policyInfoDetail?.policyType}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{fontFamily: `"Poppins", sans-serif`,}}><strong>Term:</strong></TableCell>
                    <TableCell sx={{fontFamily: `"Poppins", sans-serif`,}}>
                      {policyInfoDetail?.term === '6' ? '6 Months' : 
                       policyInfoDetail?.term === '12' ? '12 Months' : 
                       policyInfoDetail?.term}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{fontFamily: `"Poppins", sans-serif`,}}><strong>Company:</strong></TableCell>
                    <TableCell sx={{fontFamily: `"Poppins", sans-serif`,}}>{policyInfoDetail?.company}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{fontFamily: `"Poppins", sans-serif`,}}><strong>Created By:</strong></TableCell>
                    <TableCell sx={{fontFamily: `"Poppins", sans-serif`,}}>{policyInfoDetail?.createdBy?.name}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableCell>

            <TableCell>
              <Table>
                <TableBody>
                  <TableRow>
                  <TableCell  sx={{fontFamily: `"Poppins", sans-serif`,}}><strong>Line of Business:</strong></TableCell>
                  <TableCell  sx={{fontFamily: `"Poppins", sans-serif`,}}>{policyInfoDetail?.lineOfBusiness}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell  sx={{fontFamily: `"Poppins", sans-serif`,}}><strong>Effective Date:</strong></TableCell>
                  <TableCell  sx={{fontFamily: `"Poppins", sans-serif`,}}>{policyInfoDetail?.effectiveDate}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell  sx={{fontFamily: `"Poppins", sans-serif`,}}><strong>Expiration Date:</strong></TableCell>
                  <TableCell  sx={{fontFamily: `"Poppins", sans-serif`,}}>{policyInfoDetail?.expirationDate}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell  sx={{fontFamily: `"Poppins", sans-serif`,}}><strong>Updated By:</strong></TableCell>
                  <TableCell  sx={{fontFamily: `"Poppins", sans-serif`,}}>{policyInfoDetail?.updatedBy?.name}</TableCell>
                </TableRow>
                </TableBody>
              </Table>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Dialog 
        open={openEdit}
  
          onClose={(event, reason) => {
            // Prevent closing on backdrop click or pressing Escape
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              return;
            }
            setOpenEdit(false);
          }}
          maxWidth="sm"
          fullWidth
          disableScrollLock
          keepMounted={false}
          hideBackdrop={false}
          style={{ zIndex: 1300 }}
       >
        <DialogTitle>Edit Policy</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <RequiredLabel required>Policy Type</RequiredLabel>
              <TextField
                select
                fullWidth
                name="policyType"
                value={formData.policyType || ""}
                onChange={handleChange}
              >
                {policyTypeOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <RequiredLabel required>Term</RequiredLabel>
              <TextField
                select
                fullWidth
                name="term"
                value={formData.term || ""}
                onChange={handleChange}
              >
                {termOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <RequiredLabel required>Line of Business</RequiredLabel>
              <TextField
                select
                fullWidth
                name="lineOfBusiness"
                value={formData.lineOfBusiness || ""}
                onChange={handleChange}
              >
                {lineOfBusinessOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <RequiredLabel required>Company</RequiredLabel>
              <TextField
                fullWidth
                name="company"
                value={formData.company || ""}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <RequiredLabel required>Effective Date</RequiredLabel>
              <DatePickerField
                fieldName="effectiveDate"
                formData={formData}
                handleDateChange={handleDateChange}
                handleBlur={() => {}}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <RequiredLabel required>Expiration Date</RequiredLabel>
              <DatePickerField
                fieldName="expirationDate"
                formData={formData}
                handleDateChange={handleDateChange}
                handleBlur={() => {}}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenEdit(false)} variant='outlined' size='large'>Cancel</Button>
          <Button onClick={handleUpdate} variant='outlined' size='large'>Update</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PolicyDetail;
